import { useEffect, useId, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { ChevronRight } from 'lucide-react'

import { Button } from '../../ui/Button'
import { Card } from '../../ui/Card'
import { Dialog } from '../../ui/Dialog'
import { Input } from '../../ui/Field'
import type { SizeBounds } from '../../core/capacityEnvelope'
import type { GuidedDraft } from '../../core/guided'
import { parseImageRef, type ImageRef } from '../../core/imageRef'
import { applyPopularImage, checkIsStale, describePresetChanges, plaintextExposure, plaintextWarning, popularImageFor, statefulNote, type PopularImage } from '../../core/popularImages'
import { searchApps, templateEditorPath, type TemplateSummary } from '../../core/templateCatalog'
import type { RentalMode } from '../../core/rental'
import { APP_COUNT, AppResultTile, AppSearchField, BrowseAllDialogContent, OwnImageTile, countLabel, useSettledAnnouncement } from './AppCatalog'

/** A search shows at most this many tiles; "Browse all" shows the rest. */
const SEARCH_LIMIT = 12

/** The fields a preset may change, and therefore what Undo puts back. */
type Snapshot = Pick<GuidedDraft, 'image' | 'ports' | 'env' | 'cpu' | 'ram' | 'storage' | 'name' | 'volume'>

function snapshotOf(draft: GuidedDraft): Snapshot {
  return { image: draft.image, ports: draft.ports, env: draft.env, cpu: draft.cpu, ram: draft.ram, storage: draft.storage, name: draft.name, volume: draft.volume }
}

function sameSnapshot(a: Snapshot, b: Snapshot): boolean {
  return a.image === b.image && a.ports === b.ports && a.env === b.env && a.cpu === b.cpu && a.ram === b.ram && a.storage === b.storage && a.name === b.name && a.volume === b.volume
}

/** How the reference would read with a version tag, for the caution line's example. */
function pinnedExample(ref: ImageRef): string {
  return `${ref.registry === 'docker.io' ? '' : `${ref.registry}/`}${ref.repository}:1.2.0`
}

function shortDigest(digest: string): string {
  const [algorithm = '', hex = ''] = digest.split(':')
  return `${algorithm}:${hex.slice(0, 12)}`
}

/**
 * The "What are you running" card: a starting point, the image, then the name.
 *
 * The image is the decision; the name is a label. Which preset is selected is
 * derived from the image, never stored, so a restored draft and a typed image
 * light the same tile. Local state is the search, whether "Browse all" is
 * open, and a single, unpersisted Undo for the last preset applied, which
 * lapses as soon as the tenant edits anything that preset touched.
 */
export function WorkloadCard({ draft, setDraft, errors, submitted, rentalMode, sizeBounds, onNavigate }: { draft: GuidedDraft; setDraft: Dispatch<SetStateAction<GuidedDraft>>; errors: Record<string, string>; submitted: boolean; rentalMode: RentalMode | undefined; sizeBounds: SizeBounds; onNavigate?: ((path: string) => void) | undefined }) {
  const imageInput = useRef<HTMLInputElement>(null)
  const searchInput = useRef<HTMLInputElement>(null)
  const tiles = useRef<HTMLDivElement>(null)
  const chosenInDialog = useRef<string | null>(null)
  const statusId = useId()
  const [undo, setUndo] = useState<{ before: Snapshot; after: Snapshot; changes: string[]; required: string[]; note: string | null } | null>(null)
  const [query, setQuery] = useState('')
  const [browsing, setBrowsing] = useState(false)
  const current = snapshotOf(draft)
  const selected = popularImageFor(draft.image)
  const ref = parseImageRef(draft.image)
  const searching = query.trim() !== ''
  const results = searchApps(query)
  const shown = searching ? results.slice(0, SEARCH_LIMIT) : results
  const announcement = useSettledAnnouncement(searching ? countLabel(results.length) : '')
  const exposure = plaintextExposure(draft)

  // A manual edit of anything the preset set — here, in Size, in Networking or
  // in Environment — ends the offer to undo it.
  useEffect(() => {
    if (undo && !sameSnapshot(undo.after, current)) setUndo(null)
  })

  const apply = (preset: PopularImage) => {
    const next = applyPopularImage(draft, preset, { rentalMode, bounds: sizeBounds })
    const required = (preset.requiredEnv ?? []).filter((key) => next.env.some((row) => row.key.trim() === key && row.value.trim() === ''))
    setDraft(next)
    setUndo({ before: current, after: snapshotOf(next), changes: describePresetChanges(draft, next), required, note: statefulNote(preset) })
  }

  // A template is a different screen, not a preset: nothing is applied to the
  // draft, which stays in its saved draft as it is.
  const openTemplate = (template: TemplateSummary) => {
    setBrowsing(false)
    onNavigate?.(templateEditorPath(template.id))
  }

  const chooseInDialog = (preset: PopularImage) => {
    chosenInDialog.current = preset.id
    apply(preset)
    setBrowsing(false)
  }

  // After "Browse all", focus lands on the tile just applied if the card shows
  // it, and on the search otherwise.
  const focusAfterBrowsing = () => {
    const id = chosenInDialog.current
    chosenInDialog.current = null
    if (id === null) return null
    return tiles.current?.querySelector<HTMLElement>(`[data-preset="${id}"]`) ?? searchInput.current
  }

  const restore = () => {
    if (!undo) return
    const { before } = undo
    setUndo(null)
    setDraft((latest) => ({ ...latest, ...before }))
  }

  const chooseOwn = () => {
    imageInput.current?.focus()
    imageInput.current?.select()
  }

  // From an empty search: a query that reads as an image reference is one.
  const useOwnImage = () => {
    const typed = query.trim()
    if (/[/:]/.test(typed)) setDraft((latest) => ({ ...latest, image: typed }))
    imageInput.current?.focus()
  }

  return <Card title="What are you running?">
    <AppSearchField id="app-search" value={query} onChange={setQuery} inputRef={searchInput} resultsRef={tiles} />
    <span className="sr-only" aria-live="polite" aria-atomic="true">{announcement}</span>
    <div className="field">
      <div className="app-catalog-head">
        <span className="image-presets-label" aria-hidden="true">{searching ? 'Matching apps' : 'Popular'}</span>
        <Button type="button" variant="link" size="sm" className="app-browse-all" onClick={() => setBrowsing(true)}>Browse all {APP_COUNT}<ChevronRight aria-hidden="true" size={14} /></Button>
      </div>
      <div className="image-presets" role="group" aria-label="Start from" ref={tiles}>
        <OwnImageTile pressed={!selected} onClick={chooseOwn} />
        {shown.map((result) => <AppResultTile key={result.kind === 'image' ? result.preset.id : `template-${result.template.id}`} scope="card" result={result} selectedId={selected?.id} onChoosePreset={apply} onChooseTemplate={openTemplate} />)}
      </div>
      {searching && results.length === 0 && <p className="app-search-empty">
        No app matches "{query.trim()}".{' '}
        <Button type="button" variant="link" size="sm" onClick={useOwnImage}>Use your own image</Button>
      </p>}
      {results.length > shown.length && <p className="app-search-more">and {results.length - shown.length} more — refine your search</p>}
    </div>
    <Dialog open={browsing} onOpenChange={setBrowsing}>
      {browsing && <BrowseAllDialogContent initialQuery={query} selectedId={selected?.id} onChoose={chooseInDialog} onChooseTemplate={openTemplate} returnFocus={focusAfterBrowsing} />}
    </Dialog>

    <Input ref={imageInput} id="container-image" label="Container image" className="image-input" required spellCheck={false} autoCapitalize="off" autoComplete="off" value={draft.image} error={submitted ? errors.image : undefined} aria-describedby={statusId} onChange={(event) => setDraft((latest) => ({ ...latest, image: event.target.value }))} />
    {ref && <div className="image-parts" aria-hidden="true">
      <span className="image-part">{ref.registryLabel}</span>
      <span className="image-part">{ref.repository}</span>
      {(ref.tag !== null || ref.digest === null) && <span className={`image-part${ref.pinned ? '' : ' image-part--caution'}`}>{ref.tag ?? 'latest (implied)'}</span>}
      {ref.digest && <span className="image-part" title={ref.digest}>{shortDigest(ref.digest)}</span>}
    </div>}
    {/* Described, not live: this sentence names the repository, so a live
        region here would be re-read on every keystroke. The hidden region
        below changes only when the pinned state flips. */}
    <span id={statusId} className={`image-pin${ref && !ref.pinned ? ' image-pin--caution' : ''}`}>
      {ref ? ref.pinned
        ? 'Pinned. Every placement runs the same image.'
        : <>{ref.tag === null && <>No tag means <code>latest</code>. </>}<code>latest</code> can change between placements, so two instances may run different code. Use a version tag, for example <code>{pinnedExample(ref)}</code>.</>
        : null}
    </span>
    <span className="sr-only" role="status">{ref ? ref.pinned ? 'Image is pinned.' : 'Image is not pinned.' : ''}</span>
    {selected && checkIsStale(selected, new Date()) && <span className="image-pin image-pin--muted">This starting point was last checked {selected.verifiedAt.slice(0, 10)}.</span>}

    {undo && <p className="preset-applied" role="status">
      {undo.changes.length > 0 ? `Also set ${undo.changes.join(' · ')}. You can change these below.` : 'Nothing else needed changing.'}
      {undo.required.map((key) => ` Set ${key} before continuing.`).join('')}
      {undo.note && ` ${undo.note}`}{' '}
      <Button type="button" variant="link" size="sm" onClick={restore}>Undo</Button>
    </p>}

    {exposure && <div className="notice notice-caution plaintext-warning" id="plaintext-warning">
      <strong>{plaintextWarning(exposure).title}</strong>
      <p>{plaintextWarning(exposure).text}</p>
      <label className="plaintext-ack">
        <input type="checkbox" id="plaintext-ack" checked={draft.acknowledgedPlaintext === exposure.key} aria-invalid={submitted && errors.plaintext ? true : undefined} aria-describedby={submitted && errors.plaintext ? 'plaintext-ack-error' : undefined} onChange={(event) => { const checked = event.target.checked; setDraft((latest) => ({ ...latest, acknowledgedPlaintext: checked ? exposure.key : undefined })) }} />
        <span>{plaintextWarning(exposure).acknowledge}</span>
      </label>
      {submitted && errors.plaintext && <div className="field-error" id="plaintext-ack-error" role="alert">{errors.plaintext}</div>}
    </div>}

    <Input id="machine-name" label="Name" required value={draft.name} description="Only you see this. It is not sent to the host." error={submitted ? errors.name : undefined} onChange={(event) => setDraft((latest) => ({ ...latest, name: event.target.value }))} />
  </Card>
}
